/**
 * BusinessProfileCard Component
 *
 * Displays business profile information including avatar and name.
 * Used in join queue flow and other places where business info needs to be shown.
 *
 * Props:
 * - business: Business object with name and profile_image_url
 * - size: Avatar size (default: 80)
 * - showLabel: Whether to show "Business Account" label (default: true)
 * - style: Additional container styles
 */

import React from 'react';
import { View, Image } from 'react-native';
import { useTheme } from 'react-native-paper';
import { QText } from './index';
import { useOrganizationTranslation } from '../hooks/useOrganizationTranslation';

const BusinessProfileCard = ({
  business,
  size = 80,
  showLabel = true,
  style = {},
}) => {
  const theme = useTheme();
  const { t } = useOrganizationTranslation();

  if (!business) {
    return null;
  }

  return (
    <View
      style={[
        {
          alignItems: 'center',
          paddingVertical: 15,
        },
        style,
      ]}
    >
      {business.profile_image_url ? (
        <View
          style={{
            width: size,
            height: size,
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: theme.colors.primary + '20',
            marginBottom: 12,
          }}
        >
          <Image
            source={{
              uri: business.profile_image_url,
              cache: 'default',
            }}
            style={{
              width: '100%',
              height: '100%',
            }}
            onError={() => {
              console.error('error');
            }}
          />
        </View>
      ) : (
        <View
          style={{
            width: size,
            height: size,
            borderRadius: 4,
            backgroundColor: theme.colors.primary,
            marginBottom: 12,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <QText
            style={{
              fontSize: size > 60 ? 24 : 16,
              fontWeight: '600',
              color: theme.colors.whiteTxt,
            }}
          >
            {business.name ? business.name.charAt(0).toUpperCase() : 'B'}
          </QText>
        </View>
      )}

      <QText
        style={{
          fontSize: size > 60 ? 18 : 16,
          fontWeight: '600',
          color: theme.colors.secondaryText,
          textAlign: 'center',
          marginBottom: showLabel ? 4 : 0,
        }}
      >
        {business.name}
      </QText>

      {showLabel && (
        <QText
          style={{
            fontSize: 14,
            color: theme.colors.placeHolderText,
            textAlign: 'center',
          }}
        >
          {t('business_account')}
        </QText>
      )}
    </View>
  );
};

export default BusinessProfileCard;
